/**
 * «Знания» — Spec 022, round-2 screen 8. Search first («Как мы делали
 * похожее?»), then four ways in — the business, accesses, methods, materials —
 * then the proven ways of doing things. Counts only where the server gives
 * them: a method list carries no application counts, so no «применён 9×».
 * Spec 048 step 2: «Находки» — what staff brought from the internet — are knowledge too: a tile,
 * and the search finds them by words beside the rest.
 */

import { useState } from "react";
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { BarChart3, Briefcase, Building2, ChevronRight, ClipboardCheck, FileText, Inbox, KeyRound, Lightbulb, ListChecks, Search as SearchIcon, Telescope } from "lucide-react";

import { dutyLine, Experience, notesLine } from "@/api/experience";
import { Radar } from "@/api/radar";
import { Business, Cases, Ideas, Knowledge, Me, Methods, Notes, ResourceCards } from "@/api/resources";
import { SuggestionCard } from "@/components/SuggestionCard";
import { SkeletonList } from "@/components/ui/Skeleton";
import { Caption, EmptyNote, Pill, ScreenHeader } from "@/kit";
import { COUNT_LIMIT, findingsLine, itemLine, placeLabel, ringTone } from "@/lib/radar";

function plural(n: number, one: string, few: string, many: string): string {
  const d = n % 10;
  const h = n % 100;
  if (d === 1 && h !== 11) return one;
  if (d >= 2 && d <= 4 && (h < 12 || h > 14)) return few;
  return many;
}

export function KnowledgeScreen() {
  const [query, setQuery] = useState("");
  const [submitted, setSubmitted] = useState("");
  const business = useQuery({ queryKey: ["business"], queryFn: Business.get, staleTime: 60_000 });
  const resources = useQuery({ queryKey: ["resources", "all"], queryFn: () => ResourceCards.list(), staleTime: 60_000 });
  const proven = useQuery({ queryKey: ["methods", "proven", null], queryFn: () => Methods.list({ status: "proven", limit: 100 }) });
  const notes = useQuery({ queryKey: ["notes"], queryFn: Notes.list });
  // Kostya, 27.09: «А где мне посмотреть идеи?» — the first way in.
  const ideas = useQuery({ queryKey: ["ideas", "all"], queryFn: () => Ideas.list() });
  // Spec 025: the same list «Кейсы» shows with no place picked.
  const cases = useQuery({ queryKey: ["cases", "list", "live", ""], queryFn: () => Cases.list() });
  const me = useQuery({ queryKey: ["me"], queryFn: Me.get });
  const results = useQuery({
    queryKey: ["knowledge-search", submitted],
    queryFn: () => Knowledge.search(submitted, { limit: 20 }),
    enabled: submitted.length > 0,
  });
  // Spec 048 step 2: the tile counts what we take and what waits for review.
  const adopt = useQuery({ queryKey: ["radar", "list", { ring: "adopt", limit: COUNT_LIMIT }], queryFn: () => Radar.list({ ring: "adopt", limit: COUNT_LIMIT }), staleTime: 60_000 });
  const review = useQuery({ queryKey: ["radar", "list", { ring: "review", limit: COUNT_LIMIT }], queryFn: () => Radar.list({ ring: "review", limit: COUNT_LIMIT }), staleTime: 60_000 });
  const unsorted = useQuery({ queryKey: ["experience-notes"], queryFn: Experience.list, staleTime: 60_000 });
  const duty = useQuery({ queryKey: ["method-duty"], queryFn: Experience.duty, staleTime: 300_000 });
  const found = useQuery({
    queryKey: ["radar", "list", { q: submitted, limit: 10 }],
    queryFn: () => Radar.list({ q: submitted, limit: 10 }),
    enabled: submitted.length > 0,
  });

  const checked = business.data?.checked_at
    ? `проверен ${new Date(business.data.checked_at).toLocaleDateString("ru-RU", { day: "2-digit", month: "2-digit" })}`
    : "не проверен";
  const nRes = resources.data?.length;
  const nProven = proven.data?.length;
  const nNotes = notes.data?.filter((n) => !n.is_daily).length;

  const waiting = (ideas.data ?? []).filter((i) => (i.status === "new" || i.status === "reviewing") && i.author_id !== me.data?.id).length;
  const casesIn = (status: string) => (cases.data ?? []).filter((c) => c.status === status).length;
  const tiles = [
    { to: "/ideas", icon: Lightbulb, title: "Идеи", meta: ideas.data ? (waiting ? `${waiting} ${plural(waiting, "ждёт", "ждут", "ждут")} решения · всего ${ideas.data.length}` : `${ideas.data.length} ${plural(ideas.data.length, "идея", "идеи", "идей")}`) : "твои и партнёра" },
    { to: "/business", icon: Building2, title: "О бизнесе", meta: business.data ? `паспорт · ${checked}` : "паспорт бизнеса" },
    { to: "/business#resources", icon: KeyRound, title: "Доступы", meta: nRes != null ? `${nRes} ${plural(nRes, "ресурс", "ресурса", "ресурсов")}` : "ресурсы и доступы" },
    { to: "/knowledge/methods", icon: ListChecks, title: "Методы", meta: nProven != null ? `${nProven} ${plural(nProven, "проверенный", "проверенных", "проверенных")}` : "как мы это делаем" },
    { to: "/knowledge/cases", icon: Briefcase, title: "Кейсы", meta: cases.data?.length ? `на проверке ${casesIn("review")} · принято ${casesIn("accepted")}` : "примеры решений" },
    { to: "/knowledge/radar", icon: Telescope, title: "Находки", meta: findingsLine(adopt.data?.length, review.data?.length) },
    { to: "/knowledge/stats", icon: BarChart3, title: "Статистика", meta: "кто что сделал и сколько потратил" },
    // Spec 053, step 2: tasks closed without looking their experience through.
    { to: "/knowledge/experience", icon: Inbox, title: "Опыт не разобран", meta: unsorted.data ? (unsorted.data.length ? `${notesLine(unsorted.data.length)} ждут разбора` : "всё разобрано") : "опыт закрытых задач" },
    // Spec 053, step 4: Monday's look through the methods' base.
    { to: "/knowledge/duty", icon: ClipboardCheck, title: "Дежурство по методам", meta: duty.isSuccess ? dutyLine(duty.data) : "раз в неделю" },
    { to: "/inbox", icon: FileText, title: "Материалы", meta: nNotes != null ? `${nNotes} ${plural(nNotes, "заметка", "заметки", "заметок")}` : "заметки и идеи" },
  ];

  return (
    <div className="flex flex-col gap-3">
      <ScreenHeader title="Знания" />
      <form
        role="search"
        onSubmit={(e) => {
          e.preventDefault();
          setSubmitted(query.trim());
        }}
        className="flex min-h-12 items-center gap-2 rounded-xl border border-p-border-strong bg-p-surface px-3 focus-within:outline focus-within:outline-2 focus-within:outline-offset-1 focus-within:outline-p-focus"
      >
        <SearchIcon className="h-5 w-5 shrink-0 text-p-text-muted" aria-hidden="true" />
        <input
          type="search"
          enterKeyHint="search"
          value={query}
          onChange={(e) => {
            setQuery(e.target.value);
            if (!e.target.value.trim()) setSubmitted("");
          }}
          placeholder="Как мы делали похожее?"
          aria-label="Поиск по знаниям"
          className="min-w-0 flex-1 bg-transparent text-[16px] text-p-text outline-none placeholder:text-p-text-muted"
        />
      </form>

      {submitted ? (
        <section className="flex flex-col gap-2">
          <Caption>Нашлось</Caption>
          {results.isLoading && <SkeletonList count={3} />}
          {results.isError && <EmptyNote>Поиск не отработал. Попробуй ещё раз.</EmptyNote>}
          {results.data?.length === 0 && !found.data?.length && <EmptyNote>Ничего похожего. Попробуй другие слова — поиск понимает пересказ, а не только точные совпадения.</EmptyNote>}
          {(results.data ?? []).map((hit) => <SuggestionCard key={`${hit.entity_type}-${hit.entity_id}`} hit={hit} />)}
          {(found.data?.length ?? 0) > 0 && (
            <>
              <Caption>Находки</Caption>
              <ul className="overflow-hidden rounded-card border border-p-border bg-p-surface">
                {found.data!.map((item) => (
                  <li key={item.id} className="border-b border-p-border last:border-0">
                    <Link to={`/knowledge/radar?open=${item.id}`} className="flex items-start gap-3 px-4 py-3 press-surface">
                      <span className="flex min-w-0 flex-1 flex-col gap-1">
                        <span className="text-[15px] font-semibold leading-5 text-p-text">{item.title}</span>
                        <span className="text-meta text-p-text-muted">{itemLine(item)}</span>
                        <span><Pill tone={ringTone(item.ring)}>{placeLabel(item)}</Pill></span>
                      </span>
                      <ChevronRight className="mt-0.5 h-5 w-5 shrink-0 text-p-text-muted" aria-hidden="true" />
                    </Link>
                  </li>
                ))}
              </ul>
            </>
          )}
        </section>
      ) : (
        <>
          <div className="grid grid-cols-2 gap-2 lg:grid-cols-3">
            {tiles.map(({ to, icon: Icon, title, meta }) => (
              <Link key={title} to={to} className={`flex min-h-[112px] flex-col gap-1.5 rounded-card border border-p-border bg-p-surface px-4 py-3.5 press-surface`}>
                <Icon className="h-6 w-6 text-p-text" aria-hidden="true" />
                <span className="text-[17px] font-bold leading-6 text-p-text">{title}</span>
                <span className="text-meta text-p-text-muted">{meta}</span>
              </Link>
            ))}
          </div>

          <Caption>Проверенные способы</Caption>
          {proven.isLoading && <SkeletonList count={2} />}
          {proven.data?.length === 0 && (
            <EmptyNote>Проверенных способов пока нет. Метод становится проверенным, когда по нему успешно закрыли задачу.</EmptyNote>
          )}
          {(proven.data?.length ?? 0) > 0 && (
            <ul className="overflow-hidden rounded-card border border-p-border bg-p-surface">
              {proven.data!.slice(0, 8).map((m) => (
                <li key={m.id} className="border-b border-p-border last:border-0">
                  <Link to={`/knowledge/${m.id}`} className="flex items-start gap-3 px-4 py-3 press-surface">
                    <span className="min-w-0 flex-1">
                      <span className="block text-[15px] font-semibold leading-5 text-p-text">{m.title}</span>
                      {m.summary && <span className="line-clamp-2 text-meta text-p-text-muted">{m.summary}</span>}
                    </span>
                    <ChevronRight className="mt-0.5 h-5 w-5 shrink-0 text-p-text-muted" aria-hidden="true" />
                  </Link>
                </li>
              ))}
            </ul>
          )}
          {(proven.data?.length ?? 0) > 8 && (
            <Link to="/knowledge/methods" className="self-start text-body font-semibold text-p-link">Все методы</Link>
          )}
        </>
      )}
    </div>
  );
}
